"use client";

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Trash2, Key, Copy, Info, AlertTriangle, Check } from 'lucide-react';
import { apiKeyService } from '@/services/api-key-service';
import { toast } from 'sonner';

type Props = {
  serviceId: string;
  serviceName: string;
};

type ServiceApiKey = {
  id: string;
  name: string;
  start: string;
  createdAt: string;
  expiresAt?: string;
  enabled: boolean;
};

export function ServiceApiKeyPanel({ serviceId, serviceName }: Props) {
  const [apiKeys, setApiKeys] = useState<ServiceApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiresIn, setNewKeyExpiresIn] = useState<string>('');
  const [creating, setCreating] = useState(false);

  // Load service-associated API keys on mount
  useEffect(() => {
    loadServiceApiKeys();
  }, [serviceId]);

  const loadServiceApiKeys = async () => {
    setLoadingKeys(true);
    try {
      const keys = await apiKeyService.listForService(serviceId);
      setApiKeys(keys);
    } catch (err) {
      console.error('Error loading API keys:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newKeyName.trim()) {
      toast.error('API Key name is required');
      return;
    }

    setCreating(true);
    setKeyCopied(false);
    try {
      const result = await apiKeyService.createForService(serviceId, {
        name: newKeyName.trim(),
        expiresIn: newKeyExpiresIn ? parseInt(newKeyExpiresIn) : undefined,
      });

      setGeneratedKey(result.key || null);
      setNewKeyName('');
      setNewKeyExpiresIn('');

      // Reload the API keys list
      await loadServiceApiKeys();
      toast.success('Service API key created and bound - copy it now!');
    } catch (err: any) {
      console.error('Error creating API key:', err);
      toast.error(err.message || 'Failed to create API key');
      setGeneratedKey(null);
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = () => {
    if (!generatedKey) return;
    navigator.clipboard.writeText(generatedKey);
    setKeyCopied(true);
    toast.success('API key copied to clipboard');

    // Reset copy state after 3 seconds
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const handleDeleteApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This cannot be undone.')) {
      return;
    }

    try {
      await apiKeyService.delete(keyId);
      toast.success('API key deleted');
      await loadServiceApiKeys();
    } catch (err: any) {
      console.error('Error deleting API key:', err);
      toast.error(err.message || 'Failed to delete API key');
    }
  };

  return (
    <div className="space-y-6">
      {/* Information Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-900">
          <strong>Service-Scoped API Keys:</strong> API keys created here are restricted to this service.
          Currently they grant <code className="bg-blue-100 px-1 rounded">action:*</code> access to all enabled service actions.
          Future versions will allow fine-grained control based on the service manifest.
        </AlertDescription>
      </Alert>

      {/* Create Service API Key Form */}
      {!generatedKey ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Issue Service API Key
            </CardTitle>
            <CardDescription>Create a new API key scoped to this service</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateApiKey} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="serviceKeyName">API Key Name</Label>
                <Input
                  id="serviceKeyName"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., External Client, Mobile App"
                  disabled={creating}
                  autoFocus
                />
                <p className="text-sm text-muted-foreground">
                  A descriptive name for this service API key
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="serviceKeyExpiry">Expiry (Optional, in seconds)</Label>
                <Input
                  id="serviceKeyExpiry"
                  type="number"
                  value={newKeyExpiresIn}
                  onChange={(e) => setNewKeyExpiresIn(e.target.value)}
                  placeholder="e.g., 2592000 (30 days)"
                  disabled={creating}
                />
                <p className="text-sm text-muted-foreground">
                  Leave empty for no expiration
                </p>
              </div>

              <Button type="submit" disabled={creating} className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                {creating ? 'Creating...' : 'Create Service API Key'}
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        /* Generated Key Display - Force Copy */
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardHeader className="bg-amber-100">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <AlertTriangle className="w-5 h-5" />
                  Your New Service API Key
                </CardTitle>
                <CardDescription className="text-amber-800">
                  Copy this key now. You will NOT be able to see it again!
                </CardDescription>
              </div>
              {keyCopied && <Check className="w-6 h-6 text-green-600" />}
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Key Display */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">API Key Value</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-4 bg-amber-100 border-2 border-amber-200 rounded-md font-mono text-sm break-all min-h-[60px] flex items-center">
                  {showKey ? generatedKey : '••••••••••••••••••••••••••••••••••••••••••••'}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowKey(!showKey)}
                  className="shrink-0"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Copy Button - Primary Action */}
            <Button
              onClick={handleCopyKey}
              className={`w-full flex items-center justify-center gap-2 h-12 text-base font-semibold ${
                keyCopied
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {keyCopied ? (
                <>
                  <Check className="w-5 h-5" />
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  Copy API Key
                </>
              )}
            </Button>

            {/* Warning */}
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This key will never be shown again. Store it securely. If you lose it, you'll need to delete and create a new one.
              </AlertDescription>
            </Alert>

            {/* Action after copy */}
            {keyCopied && (
              <Button
                onClick={() => {
                  setGeneratedKey(null);
                  setShowKey(false);
                }}
                variant="outline"
                className="w-full"
              >
                Done - Close
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Service API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Service API Keys
          </CardTitle>
          <CardDescription>
            API keys associated with {serviceName}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingKeys ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-1">No API Keys</h3>
              <p className="text-muted-foreground">
                No API keys have been created for this service yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{key.name}</div>
                    <div className="text-sm text-muted-foreground space-y-1 mt-1">
                      <div>Starts with: <span className="font-mono bg-muted px-2 py-1 rounded">{key.start}</span></div>
                      <div>Created: {new Date(key.createdAt).toLocaleDateString()}</div>
                      {key.expiresAt && (
                        <div className={new Date(key.expiresAt) < new Date() ? 'text-destructive' : ''}>
                          Expires: {new Date(key.expiresAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {!key.enabled && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded">
                        Disabled
                      </span>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteApiKey(key.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Future Improvements Documentation */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-amber-900">Future Improvements</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-amber-900 space-y-2">
          <p>
            <strong>🎯 Planned Features:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              <strong>Action-Level Control:</strong> Restrict API keys to specific actions defined in the service manifest
              (e.g., read-only, write-only, specific endpoints)
            </li>
            <li>
              <strong>Service Manifest Integration:</strong> Parse service manifest to show available actions and allow granular permissions
            </li>
            <li>
              <strong>Rate Limiting per Key:</strong> Set different rate limits for different API keys
            </li>
            <li>
              <strong>Key Rotation:</strong> Automatic key rotation with grace periods
            </li>
            <li>
              <strong>Usage Analytics:</strong> Track API key usage, success rates, and error logs
            </li>
            <li>
              <strong>Audit Logging:</strong> Complete audit trail of API key creation, usage, and deletion
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
